import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FiltersPagination from "../../components/elements/FiltersPagination";
import DataTableElement from "../../components/elements/DataTableElement";
import DatePicker from "react-datepicker";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

import {
    clientsResource,
    paymentsResource,
    getDataHome,
} from "../../helpers/configRoutes/backend_helper";

import ColumnsSubscriptions from "./ColumnsSubscriptions";
import { currencyUSD } from "../../helpers/forms/formater";

// cards
import { StatCard } from "../../components/cards/StatCard";
import { AlertCard } from "../../components/cards/AlertCard";

const Home = () => {
    const navigate = useNavigate();
    const date = new Date();

    // filters
    const [filtersPayment, setFiltersPayment] = useState({
        month: date,
        home: true,
    });

    const [filters, setFilters] = useState({
        perPage: 10,
        page: 1,
        search: "",
    });

    // table
    const [subscriptions, setSubscriptions] = useState([]);
    const [meta, setMeta] = useState({ total: 0, current_page: 1 });
    const [tableLoad, setTableLoad] = useState({
        show: false,
        textLoad: "Loading data...",
    });

    // dashboard data
    const [dataHome, setDataHome] = useState({});
    const [chartData, setChartData] = useState([{ name: "1", sales: 1 },
        { name: "11", sales: 12 }
    ]);

    /* =========================
        EFFECTS
    ========================= */

    useEffect(() => {
        const getHomeInfo = async () => {
            const response = await getDataHome();
            setDataHome(response);
        };
        getHomeInfo();
    }, []);

    useEffect(() => {
        const getPayments = async () => {
            try {
                const response = await paymentsResource("get", {
                    params: filtersPayment,
                });
                setChartData(response.chartData ?? []);
            } catch (e) {
                console.error(e);
            }
        };
        getPayments();
    }, [filtersPayment]);

    useEffect(() => {
        const getClients = async () => {
            setTableLoad({ show: true, textLoad: "Loading subscriptions..." });
            const response = await clientsResource("get", { params: filters });
            setSubscriptions(response.companies ?? []);
            setMeta(response.meta ?? meta);
            setTableLoad({ show: false });
        };
        getClients();
    }, [filters]);

    const cols = ColumnsSubscriptions();
    const alerts = dataHome.alerts ?? {
        expired_subscriptions: dataHome.subscriptions?.expired ?? 0,
        subscriptions_expiring: dataHome.subscriptions?.expiring ?? 0,
        trial_subscriptions: dataHome.subscriptions?.trial ?? 0,
    };
    const kpis = dataHome.kpis ?? {
        active_subscriptions: dataHome.subscriptions?.active ?? 0,
        completed_loads: dataHome.loads?.completed ?? 0,
    };

    /* =========================
        RENDER
    ========================= */

    return (
        <div className="p-6 bg-cs-bg min-h-full animate-fadeIn">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-semibold text-cs-dark">
                    Operations Dashboard
                </h1>
                <p className="text-sm text-cs-muted">
                    Real-time overview of subscriptions, revenue and completed loads
                </p>
            </div>

            {/* ALERTS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <AlertCard
                    title="Expired subscriptions"
                    value={alerts.expired_subscriptions ?? 0}
                    color="cs-danger"
                    link="/clients?status=expired"
                />
                <AlertCard
                    title="Subscriptions expiring"
                    value={alerts.subscriptions_expiring ?? 0}
                    color="cs-warning"
                    link="/clients?status=expiring"
                />
                <AlertCard
                    title="Trial subscriptions"
                    value={alerts.trial_subscriptions ?? 0}
                    color="cs-primary-dark"
                    link="/clients"
                />
            </div>

            {/* STATS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <StatCard
                    label={`Revenue ${dataHome.sales?.month ?? ''}`}
                    value={currencyUSD(dataHome.sales?.last_month ?? 0)}
                    trend={`${dataHome.sales?.percentage_last_month ?? 0}% vs ${dataHome.sales?.lastMonth ?? ''}`}
                />

                <StatCard
                    label={`Projected month ${dataHome.sales?.next_month ?? ''}`}
                    value={`$${dataHome.sales?.projected_next_month ?? 0}`}
                    className="bg-cs-surface border border-cs-border"
                    subText="Based on current loads & subscriptions"
                />


                <StatCard
                    label="Active Subscriptions"
                    value={kpis.active_subscriptions ?? 0}
                />

                <StatCard
                    label="Completed loads"
                    value={kpis.completed_loads ?? 0}
                />
            </div>

            {/* CHART */}
            <div className="bg-cs-surface border border-cs-border rounded-xl p-6 mb-10">
                <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-cs-dark">
                            Revenue by day
                        </h2>
                        <p className="text-sm text-cs-muted">
                            Completed loads revenue
                        </p>
                    </div>

                    <DatePicker
                        selected={filtersPayment.month}
                        onChange={(e) =>
                            setFiltersPayment({
                                ...filtersPayment,
                                month: e,
                            })
                        }
                        showMonthYearPicker
                        dateFormat="MM/yyyy"
                        className="mt-2 sm:mt-0 px-3 py-2 bg-cs-bg-alt border border-cs-border rounded-lg text-sm"
                    />
                </div>

                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Line
                            type="monotone"
                            dataKey="sales"
                            stroke="#D66A1F"
                            strokeWidth={3}
                            activeDot={{ r: 6 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* TABLE */}
            <div className="bg-cs-surface border border-cs-border rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-cs-dark">
                        Active Subscriptions
                    </h2>

                    <button
                        onClick={() => navigate("/subscriptions")}
                        className="text-sm text-cs-primary hover:underline"
                    >
                        View all →
                    </button>
                </div>

                <FiltersPagination
                    filters={filters}
                    setFilters={setFilters}
                    meta={meta}
                    showFilters
                >
                    <DataTableElement
                        columns={cols}
                        items={subscriptions}
                        tableLoad={tableLoad}
                    />
                </FiltersPagination>
            </div>
        </div>
    );
};

export default Home;
